import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Ref } from 'vue'
import axios from 'axios'
import { useApiCredentials } from '@/composables/useApiCredentials'
import useWrapper from '@/composables/useWrapper'
import { getWebSocketUrl } from '@/composables/useWebSocketUrl'

export interface KnowledgeBase {
    id: number
    app_id: number
    name: string
    description?: string
    index_name: string
    embedding_model_id: number
    embedding_model_name: string
    vectorstore_config_id: number
    vectorstore_config_name: string
    status: 'active' | 'building' | 'error' | 'deleted'
    chunk_size: number
    chunk_overlap: number
    created_at: string
    updated_at: string
    documents?: KnowledgeBaseDocument[]
}

export interface KnowledgeBaseDocument {
    id: number
    knowledge_base_id: number
    filename: string
    file_type: string
    status: 'pending' | 'processing' | 'completed' | 'error'
    chunk_count?: number
    error_message?: string
    created_at: string
    updated_at: string
}

export interface CreateKnowledgeBaseRequest {
    name: string
    description?: string
    index_name: string
    embedding_model_id: number | null
    vectorstore_config_id: number | null
    chunk_size?: number
    chunk_overlap?: number
}

export interface UpdateKnowledgeBaseRequest {
    name?: string
    description?: string
    status?: string
}

export interface KnowledgeBaseSearchResult {
    content: string
    score: number
    source: string
    metadata?: Record<string, any>
}

export interface IngestionEvent {
    event_id: string
    task_id: string
    event_type: string
    message: string
    data?: Record<string, any>
    timestamp: number
    sequence: number
}

export type IngestionStatus = 'idle' | 'pending' | 'running' | 'completed' | 'error'

export const useKnowledgeBaseStore = defineStore('knowledgeBaseStore', () => {
    const knowledgeBases: Ref<KnowledgeBase[]> = ref([])
    const currentKb: Ref<KnowledgeBase | null> = ref(null)
    const documents: Ref<KnowledgeBaseDocument[]> = ref([])
    const searchResults: Ref<KnowledgeBaseSearchResult[]> = ref([])
    const initialized: Ref<boolean> = ref(false)
    const isLoading: Ref<boolean> = ref(false)
    const isUploading: Ref<boolean> = ref(false)
    const isSearching: Ref<boolean> = ref(false)
    const embeddingModels: Ref<{ id: number; name: string }[]> = ref([])
    const vectorstoreConfigs: Ref<{ id: number; name: string }[]> = ref([])

    // Ingestion WebSocket state
    const ingestionTaskId: Ref<string | null> = ref(null)
    const ingestionStatus: Ref<IngestionStatus> = ref('idle')
    const ingestionEvents: Ref<IngestionEvent[]> = ref([])
    const ingestionWebSocket: Ref<WebSocket | null> = ref(null)
    const isIngestionConnected: Ref<boolean> = ref(false)
    const ingestionError: Ref<string | null> = ref(null)

    const { rootGuid, rootAxiosBearerHeader, portalBaseUrl } = useApiCredentials()
    const { handleAxiosError, rootAuth } = useWrapper()

    const baseRoute = computed(() => `${portalBaseUrl}/${rootGuid.value}/knowledge-base`)

    // Load all knowledge bases
    const loadKnowledgeBases = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            isLoading.value = true
            try {
                const { data } = await axios.get<KnowledgeBase[]>(
                    baseRoute.value,
                    rootAxiosBearerHeader.value
                )
                knowledgeBases.value = data
                initialized.value = true
            } finally {
                isLoading.value = false
            }
        }, true),
        { errorMsg: 'stores.knowledgeBase.reload.error' }
    )

    // Load single knowledge base with documents
    const loadKnowledgeBase = handleAxiosError(
        rootAuth(async (kbId: number): Promise<void> => {
            isLoading.value = true
            try {
                const { data } = await axios.get<KnowledgeBase>(
                    `${baseRoute.value}/${kbId}`,
                    rootAxiosBearerHeader.value
                )
                currentKb.value = data
                await loadDocuments(kbId)
            } finally {
                isLoading.value = false
            }
        }, true),
        { errorMsg: 'stores.knowledgeBase.reload.error' }
    )

    // Create knowledge base
    const createKnowledgeBase = handleAxiosError(
        rootAuth(async (payload: CreateKnowledgeBaseRequest): Promise<void> => {
            const { data } = await axios.post<KnowledgeBase>(
                baseRoute.value,
                payload,
                rootAxiosBearerHeader.value
            )
            knowledgeBases.value.push(data)
        }, true),
        {
            errorMsg: 'stores.knowledgeBase.create.error',
            successMsg: 'stores.knowledgeBase.create.success'
        }
    )

    // Update knowledge base
    const updateKnowledgeBase = handleAxiosError(
        rootAuth(async (kbId: number, payload: UpdateKnowledgeBaseRequest): Promise<void> => {
            await axios.put(
                `${baseRoute.value}/${kbId}`,
                payload,
                rootAxiosBearerHeader.value
            )
            await loadKnowledgeBases()
        }, true),
        {
            errorMsg: 'stores.knowledgeBase.update.error',
            successMsg: 'stores.knowledgeBase.update.success'
        }
    )

    // Delete knowledge base
    const deleteKnowledgeBase = handleAxiosError(
        rootAuth(async (kbId: number): Promise<void> => {
            await axios.delete(
                `${baseRoute.value}/${kbId}`,
                rootAxiosBearerHeader.value
            )
            knowledgeBases.value = knowledgeBases.value.filter(kb => kb.id !== kbId)
            if (currentKb.value?.id === kbId) {
                currentKb.value = null
                documents.value = []
            }
        }, true),
        {
            errorMsg: 'stores.knowledgeBase.remove.error',
            successMsg: 'stores.knowledgeBase.remove.success'
        }
    )

    // Load documents for a knowledge base
    const loadDocuments = handleAxiosError(
        rootAuth(async (kbId: number): Promise<void> => {
            const { data } = await axios.get<KnowledgeBaseDocument[]>(
                `${baseRoute.value}/${kbId}/documents`,
                rootAxiosBearerHeader.value
            )
            documents.value = data
        }, true),
        { errorMsg: 'stores.knowledgeBase.documents.loadError' }
    )

    // Upload documents
    const uploadDocuments = handleAxiosError(
        rootAuth(async (kbId: number, files: File[]): Promise<void> => {
            isUploading.value = true
            try {
                const formData = new FormData()
                files.forEach(file => formData.append('files', file))
                const { data } = await axios.post<{ task_id: string; detail: string; document_ids: number[] }>(
                    `${baseRoute.value}/${kbId}/documents`,
                    formData,
                    {
                        ...rootAxiosBearerHeader.value,
                        headers: {
                            ...rootAxiosBearerHeader.value.headers,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                )
                await loadDocuments(kbId)

                // Connect WebSocket for live ingestion progress
                if (data.task_id) {
                    ingestionTaskId.value = data.task_id
                    ingestionStatus.value = 'pending'
                    ingestionEvents.value = []
                    ingestionError.value = null
                    connectIngestionWebSocket(data.task_id)
                }
            } finally {
                isUploading.value = false
            }
        }, true),
        {
            errorMsg: 'stores.knowledgeBase.documents.uploadError',
            successMsg: 'stores.knowledgeBase.documents.uploadSuccess'
        }
    )

    // Delete document
    const deleteDocument = handleAxiosError(
        rootAuth(async (kbId: number, docId: number): Promise<void> => {
            await axios.delete(
                `${baseRoute.value}/${kbId}/documents/${docId}`,
                rootAxiosBearerHeader.value
            )
            documents.value = documents.value.filter(d => d.id !== docId)
        }, true),
        {
            errorMsg: 'stores.knowledgeBase.documents.deleteError',
            successMsg: 'stores.knowledgeBase.documents.deleteSuccess'
        }
    )

    // Search knowledge base
    const searchKnowledgeBase = handleAxiosError(
        rootAuth(async (kbId: number, query: string, topK: number = 5): Promise<void> => {
            isSearching.value = true
            try {
                const { data } = await axios.post<{ documents: Array<{ content: string; score: number; metadata?: Record<string, any> }> }>(
                    `${baseRoute.value}/${kbId}/search`,
                    { query, k: topK },
                    rootAxiosBearerHeader.value
                )
                searchResults.value = (data.documents || []).map(doc => ({
                    content: doc.content,
                    score: doc.score,
                    source: doc.metadata?.filename || '',
                    metadata: doc.metadata,
                }))
            } finally {
                isSearching.value = false
            }
        }, true),
        { errorMsg: 'stores.knowledgeBase.search.error' }
    )

    // Load available embedding models and vectorstore configs
    const loadAvailableConfigs = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            const { data } = await axios.get<{ embedding_models: { id: number; name: string }[], vectorstore_configs: { id: number; name: string }[] }>(
                `${baseRoute.value}/availability`,
                rootAxiosBearerHeader.value
            )
            embeddingModels.value = data.embedding_models || []
            vectorstoreConfigs.value = data.vectorstore_configs || []
        }, true),
        { errorMsg: 'stores.knowledgeBase.reload.error' }
    )

    // ==================== INGESTION WEBSOCKET ====================

    let heartbeatInterval: ReturnType<typeof setInterval> | null = null

    const connectIngestionWebSocket = (taskId: string): void => {
        disconnectIngestionWebSocket()

        const token = rootAxiosBearerHeader.value.headers['Authorization']?.replace('Bearer ', '')
        const kbPrefix = import.meta.env.VITE_KB_URL || '/kb'
        const wsUrl = getWebSocketUrl(`${kbPrefix}/?guid=${rootGuid.value}&task_id=${taskId}&token=${token}`)


        try {
            const ws = new WebSocket(wsUrl)
            ingestionWebSocket.value = ws

            ws.onopen = () => {
                isIngestionConnected.value = true
                ingestionError.value = null
                ingestionStatus.value = 'running'
                startIngestionHeartbeat()
            }

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    handleIngestionMessage(data)
                } catch {
                    if (event.data === 'heartbeat_ack') return
                    console.error('KB: Failed to parse WebSocket message:', event.data)
                }
            }

            ws.onerror = (error) => {
                console.error('KB: WebSocket error:', error)
                ingestionError.value = 'WebSocket connection error'
            }

            ws.onclose = (event) => {
                isIngestionConnected.value = false
                ingestionWebSocket.value = null
                stopIngestionHeartbeat()

                // Auto-reconnect if ingestion is still running
                if (event.code !== 1000 && ingestionStatus.value === 'running' && ingestionTaskId.value) {
                    setTimeout(() => {
                        if (ingestionTaskId.value && ingestionStatus.value === 'running') {
                            connectIngestionWebSocket(ingestionTaskId.value)
                        }
                    }, 3000)
                }
            }
        } catch (error) {
            console.error('KB: Failed to connect WebSocket:', error)
            ingestionError.value = 'Failed to establish WebSocket connection'
        }
    }

    const handleIngestionMessage = (data: any): void => {
        if (data.event_type === 'heartbeat_ack' || data === 'heartbeat_ack') return
        if (data.event_type === 'connection_established') return
        if (data.event_type === 'ping') return

        const ingestionEvent: IngestionEvent = {
            event_id: data.event_id || crypto.randomUUID(),
            task_id: data.task_id || ingestionTaskId.value || '',
            event_type: data.event_type || 'unknown',
            message: data.message || '',
            data: data.data || {},
            timestamp: data.timestamp || Date.now() / 1000,
            sequence: data.sequence || ingestionEvents.value.length
        }

        ingestionEvents.value.push(ingestionEvent)

        // Update ingestion status from event type
        const eventType = ingestionEvent.event_type.toLowerCase()

        // Update individual document status from node events
        if (ingestionEvent.data?.document_id) {
            const docId = Number(ingestionEvent.data.document_id)
            const doc = documents.value.find(d => d.id === docId)
            if (doc) {
                if (eventType.includes('node_completed')) {
                    doc.status = 'completed'
                    if (ingestionEvent.data.chunk_count != null) {
                        doc.chunk_count = ingestionEvent.data.chunk_count
                    }
                } else if (eventType.includes('node_error')) {
                    doc.status = 'error'
                } else if (eventType.includes('node_started')) {
                    doc.status = 'processing'
                }
            }
        }

        if (eventType.includes('workflow_completed') || eventType === 'workflow_completed') {
            ingestionStatus.value = 'completed'
            // Optimistically update KB status locally
            if (currentKb.value) {
                currentKb.value.status = 'active'
                // Also update in the list
                const kbInList = knowledgeBases.value.find(kb => kb.id === currentKb.value!.id)
                if (kbInList) kbInList.status = 'active'
            }
            // Auto-disconnect after completion
            setTimeout(() => disconnectIngestionWebSocket(), 2000)
        } else if (eventType.includes('workflow_error') || eventType === 'workflow_error') {
            ingestionStatus.value = 'error'
            ingestionError.value = ingestionEvent.message
            // Optimistically update KB status locally
            if (currentKb.value) {
                currentKb.value.status = 'error'
                const kbInList = knowledgeBases.value.find(kb => kb.id === currentKb.value!.id)
                if (kbInList) kbInList.status = 'error'
            }
        } else if (eventType.includes('started') || eventType.includes('executed')) {
            ingestionStatus.value = 'running'
        }
    }

    const startIngestionHeartbeat = (): void => {
        stopIngestionHeartbeat()
        heartbeatInterval = setInterval(() => {
            if (ingestionWebSocket.value?.readyState === WebSocket.OPEN) {
                ingestionWebSocket.value.send('heartbeat')
            }
        }, 25000)
    }

    const stopIngestionHeartbeat = (): void => {
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval)
            heartbeatInterval = null
        }
    }

    const disconnectIngestionWebSocket = (): void => {
        stopIngestionHeartbeat()
        if (ingestionWebSocket.value) {
            ingestionWebSocket.value.close(1000, 'Client disconnect')
            ingestionWebSocket.value = null
        }
        isIngestionConnected.value = false
    }

    const clearIngestionState = (): void => {
        disconnectIngestionWebSocket()
        ingestionTaskId.value = null
        ingestionStatus.value = 'idle'
        ingestionEvents.value = []
        ingestionError.value = null
    }

    // NOTE: auto-loading removed — callers must invoke loadKnowledgeBases()
    // only when the knowledge_base service is enabled for the current application.

    return {
        // State
        knowledgeBases,
        currentKb,
        documents,
        searchResults,
        initialized,
        isLoading,
        isUploading,
        isSearching,
        embeddingModels,
        vectorstoreConfigs,

        // Ingestion WebSocket state
        ingestionTaskId,
        ingestionStatus,
        ingestionEvents,
        isIngestionConnected,
        ingestionError,

        // Actions
        loadKnowledgeBases,
        loadKnowledgeBase,
        createKnowledgeBase,
        updateKnowledgeBase,
        deleteKnowledgeBase,
        loadDocuments,
        uploadDocuments,
        deleteDocument,
        searchKnowledgeBase,
        loadAvailableConfigs,
        disconnectIngestionWebSocket,
        clearIngestionState
    }
})
